/* eslint-disable jira/filename-case */
/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { Component, type RefAttributes } from 'react';
import { di } from 'react-magnetic-di';
import getMeta from '@jira/platform__utils-get-meta/src/utils/get-meta/index.tsx';
import withAnalyticsEvents, {
	type WithAnalyticsEventsProps,
} from '@atlaskit/analytics-next/withAnalyticsEvents';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import getScopeData from '@atlassian/jira-common-navigation/src/get-scope-data/index.tsx';
import type { ScopeData } from '@atlassian/jira-common-navigation/src/get-scope-data/types.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { withFlagService, type FlagService } from '@atlassian/jira-flags';
import { fireOperationalAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { withRouter, type RouteContext } from '@atlassian/react-resource-router';
import { AtlBrowserStorageSession } from '@atlassian/browser-storage-controls/web-storage-service';
import { fg } from '@atlassian/jira-feature-gating';
import { errorFlag } from './errorFlag.tsx';

const THRESHOLD = 30 * 1000;
type Props = RouteContext & {
	flagService: FlagService;
	createAnalyticsEvent: (arg1: { action: string }) => UIAnalyticsEvent;
};
const getScopeType = (scope: ScopeData) => {
	switch (scope.identifierType) {
		case 'boardId':
			return 'board';
		case 'projectId':
		case 'projectKey':
			return scope.pageType === 'settings' ? 'projectSettings' : 'project';
		default:
			return '';
	}
};
/**
 * Manages URL-based redirection to support legacy routing systems while preventing redirect loops.
 * It employs session storage to track recent redirection attempts, identifying and mitigating potential loops
 * by logging relevant analytics and halting further redirects within a specified time frame.
 */
// eslint-disable-next-line jira/react/no-class-components
export class RedirectToLegacyRouting extends Component<Props> {
	componentDidMount(): void {
		// eslint-disable-next-line jira/web-storage/no-direct-web-storage-usage
		di(sessionStorage, window);
		const {
			location: { pathname, search },
			flagService,
			createAnalyticsEvent,
		} = this.props;
		const redirectPathname = pathname.startsWith('//') ? pathname.substr(1) : pathname;
		const lastRedirectTime = fg('jfp-vulcan-browser-storage-migration')
			? Number(AtlBrowserStorageSession.getItem(redirectPathname))
			: // eslint-disable-next-line jira/web-storage/no-direct-web-storage-usage
				Number(sessionStorage.getItem(redirectPathname));

		if (!lastRedirectTime) {
			if (fg('jfp-vulcan-browser-storage-migration')) {
				// @browser-storage setter
				AtlBrowserStorageSession.setStrictlyNecessaryItem(redirectPathname, Date.now().toString());
			} else {
				// eslint-disable-next-line jira/web-storage/no-direct-web-storage-usage
				sessionStorage.setItem(redirectPathname, Date.now().toString());
			}
		} else if (Date.now() - lastRedirectTime < THRESHOLD) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			const scopeData = getScopeData(window.location.href, getMeta);
			const scope = getScopeType(scopeData);
			log.safeErrorWithoutCustomerData(
				'redirect-loop.error',
				'Redirect error caused by invalid url',
				{
					scope,
				},
			);
			fireOperationalAnalytics(
				createAnalyticsEvent({
					action: 'entered',
				}),
				'redirect-loop entered',
				'endeavour.spa.redirect-loop',
				{
					scope,
				},
			);
			flagService.showFlag(errorFlag());
			if (fg('jfp-vulcan-browser-storage-migration')) {
				AtlBrowserStorageSession.removeItem(redirectPathname);
			} else {
				// eslint-disable-next-line jira/web-storage/no-direct-web-storage-usage
				sessionStorage.removeItem(redirectPathname);
			}
			return;
		} else if (fg('jfp-vulcan-browser-storage-migration')) {
			AtlBrowserStorageSession.removeItem(redirectPathname);
		} else {
			// eslint-disable-next-line jira/web-storage/no-direct-web-storage-usage
			sessionStorage.removeItem(redirectPathname);
		}

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.location.assign(redirectPathname + search);
	}

	render(): null {
		return null;
	}
}
const defaultExport: {
	(
		props: Pick<
			Omit<
				Omit<
					RouteContext & {
						flagService: FlagService;
						createAnalyticsEvent: (arg1: { action: string }) => UIAnalyticsEvent;
					},
					keyof WithAnalyticsEventsProps
				> &
					// oxlint-disable-next-line @typescript-eslint/no-explicit-any
					RefAttributes<any>,
				'flagService'
			>,
			// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			keyof RouteContext | keyof RefAttributes<any>
		> & {} & {},
	): React.JSX.Element;
	displayName: string;
	// @ts-expect-error - TS2345: argument not assignable (iso-decl artefact)
} = withRouter(withFlagService(withAnalyticsEvents()(RedirectToLegacyRouting)));
export default defaultExport;
